import { Geolocation, type PermissionStatus } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';

export interface LocationPosition {
    latitude: number;
    longitude: number;
}

/**
 * Service de géolocalisation unifié
 * Bascule automatiquement entre les APIs web et natives de Capacitor.
 */
export const getCurrentPosition = async (): Promise<LocationPosition> => {
    if (Capacitor.isNativePlatform()) {
        try {
            // Position précise ou approximative : l'une ou l'autre suffit
            const isGranted = (p: PermissionStatus) => p.location === 'granted' || p.coarseLocation === 'granted';
            let permission = await Geolocation.checkPermissions();
            if (!isGranted(permission)) {
                permission = await Geolocation.requestPermissions();
            }
            // Refus : on s'arrête là, sinon getCurrentPosition redemande la permission (double invite)
            if (!isGranted(permission)) {
                throw new Error('Permission de localisation refusée.');
            }

            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000
            });

            return {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
            };
        } catch (error) {
            console.error('Capacitor Geolocation Error:', error);
            throw new Error("Impossible d'accéder au GPS natif.");
        }
    } else {
        // Fallback Web standard
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error("Géolocalisation non supportée par le navigateur."));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                },
                (error) => {
                    console.error('Web Geolocation Error:', error);
                    reject(new Error("Accès GPS refusé ou indisponible."));
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        });
    }
};

